/**
 * Ranking da viagem (rodada 2, item 4) — o vocabulário dos títulos.
 *
 * Regra do PO: só entra título que tenha MEDIDA EXATA — uma coluna no banco
 * que diz quem fez. Nada de "quem mais aproveitou". Empate divide o título;
 * ninguém fez ainda, o título fica esperando. A pessoa que se oculta some do
 * ranking inteiro (lista, títulos e medalhas), sem aviso a ninguém.
 *
 * Os nomes são dele; os ícones são os do conjunto atual até ele desenhar os
 * seus. Nomes e descrições vivem aqui para a API e a tela dizerem o mesmo.
 */

export const RANKING_TITLES = [
  'fotografo',
  'casa_nas_costas',
  'mala_pronta',
  'guia_turistico',
  'pergunta_tudo',
  'eleitor_assiduo',
  'organizador',
  'pontual',
  'tesoureiro',
  'megafone',
] as const;

export type RankingTitle = (typeof RANKING_TITLES)[number];

export interface RankingTitleLabel {
  nome: string;
  /** O que se mede, em uma linha — aparece sob o nome. */
  medida: string;
  /** Como a contagem é dita: `unidade(n)`. */
  unidade: (n: number) => string;
  /** Ícone do conjunto atual (`<Icon name>`). */
  icone: string;
}

const plural = (s: string, p: string) => (n: number) => `${n} ${n === 1 ? s : p}`;

export const RANKING_TITLE_LABELS: Record<RankingTitle, RankingTitleLabel> = {
  fotografo: { nome: 'Fotógrafo', medida: 'quem mais enviou fotos', unidade: plural('foto', 'fotos'), icone: 'memorias' },
  casa_nas_costas: { nome: 'Trouxe a Casa nas Costas', medida: 'quem mais adicionou itens à bagagem', unidade: plural('item', 'itens'), icone: 'mala' },
  mala_pronta: { nome: 'Mala Pronta', medida: 'quem mais marcou "já está na mala"', unidade: plural('item', 'itens'), icone: 'mala' },
  guia_turistico: { nome: 'Guia Turístico', medida: 'quem mais criou atividades', unidade: plural('atividade', 'atividades'), icone: 'roteiro' },
  pergunta_tudo: { nome: 'Pergunta Tudo', medida: 'quem mais criou enquetes', unidade: plural('enquete', 'enquetes'), icone: 'enquete' },
  eleitor_assiduo: { nome: 'Eleitor Assíduo', medida: 'quem mais votou', unidade: plural('voto', 'votos'), icone: 'concluir' },
  organizador: { nome: 'Organizador', medida: 'quem mais marcou pontos de encontro', unidade: plural('ponto', 'pontos'), icone: 'ponto' },
  pontual: { nome: 'Pontual', medida: 'mais vezes o 1º a tocar "Cheguei"', unidade: plural('vez', 'vezes'), icone: 'horario' },
  tesoureiro: { nome: 'Tesoureiro', medida: 'quem mais registrou despesas', unidade: plural('despesa', 'despesas'), icone: 'documento' },
  megafone: { nome: 'Megafone', medida: 'quem mais mandou avisos', unidade: plural('aviso', 'avisos'), icone: 'notificacoes' },
};

/** Medalhas por tipo de gasto: as categorias que o Financeiro já tem. */
export const RANKING_CATEGORY_MEDALS = ['alimentacao', 'transporte', 'hospedagem', 'ingressos', 'compras', 'outros'] as const;
export type RankingCategory = (typeof RANKING_CATEGORY_MEDALS)[number];

export const RANKING_CATEGORY_LABELS: Record<RankingCategory, { nome: string; medida: string; icone: string }> = {
  alimentacao: { nome: 'Garfo de Ouro', medida: 'maior gasto em alimentação', icone: 'dinheiro' },
  transporte: { nome: 'Piloto', medida: 'maior gasto em transporte', icone: 'carro' },
  hospedagem: { nome: 'Anfitrião', medida: 'maior gasto em hospedagem', icone: 'local' },
  ingressos: { nome: 'Passaporte VIP', medida: 'maior gasto em ingressos', icone: 'carimbo' },
  compras: { nome: 'Sacoleiro', medida: 'maior gasto em compras', icone: 'mala' },
  outros: { nome: 'Faz-Tudo', medida: 'maior gasto em outros', icone: 'maisOpcoes' },
};

/** O título principal. */
export const RANKING_MAIN_TITLE = { nome: 'Top Comprador', medida: 'soma do que cada pessoa registrou (compartilhado + privado)' } as const;

/**
 * Quem leva o título: todos os que empatam no maior valor; ninguém quando o
 * maior valor é zero. Puro, para a API e os testes concordarem.
 */
export function winnersOf<T extends { memberId: string; value: number }>(rows: readonly T[]): T[] {
  const max = rows.reduce((m, r) => (r.value > m ? r.value : m), 0);
  if (max <= 0) return [];
  return rows.filter((r) => r.value === max);
}
